import React from 'react';
import { PaymentSettings } from '../../../../types/payment.types';
import { WizardData } from '../NewPOSWizard';
import ChainTokenList from '../../../../components/ui/ChainTokenList';
import FormField from '../../../../components/ui/FormField';
import { toggleChainSelection, toggleTokenSelection } from '../../../../hooks/useChainTokenToggle';
import { useI18n } from '../../../../i18n/I18n';

interface Step1Props {
    formData: WizardData;
    setFormData: React.Dispatch<React.SetStateAction<WizardData>>;
    paymentSettings: PaymentSettings | null;
    onNext: () => void;
    onCancel: () => void;
}

export default function Step1Concept({ formData, setFormData, paymentSettings, onNext, onCancel }: Step1Props) {
    const { t } = useI18n();
    const handleToggleChain = (chainId: string) => {
        setFormData(prev => toggleChainSelection(prev, chainId, paymentSettings?.chains));
    };

    const handleToggleToken = (chainId: string, tokenSymbol: string) => {
        setFormData(prev => toggleTokenSelection(prev, chainId, tokenSymbol));
    };

    return (
        <div className="flex-1 flex flex-col min-h-0 h-full w-full animate-in fade-in slide-in-from-right duration-300">
            {/* Header Area */}
            <div className="mb-4 flex-shrink-0 px-6">
                <h2 className="text-2xl font-bold text-txt-heading mb-2">{t('wizard.step1Title')}</h2>
                <div className="flex flex-col gap-2">
                    <p className="text-txt-secondary text-sm">{t('wizard.step1Subtitle')}</p>
                    <div className="h-1 w-full rounded-full overflow-hidden" style={{ background: 'var(--card-bg)' }}>
                        <div className="h-full bg-electric-blue w-1/2 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                    </div>
                </div>
            </div>

            {/* Scrollable Content + Sticky Actions */}
            <div className="flex-1 overflow-y-auto w-full no-scrollbar">
                <div className="space-y-6 pb-36 px-6">
                    <FormField label={t('wizard.name')}>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={t('wizard.namePlaceholder')}
                            className="w-full bg-transparent border-none text-txt-heading text-lg placeholder:text-txt-muted focus:ring-0 focus:outline-none p-0"
                        />
                    </FormField>

                    <FormField
                        label={t('wizard.walletAddress')}
                        badge={
                            <span className="text-[10px] font-bold text-electric-blue bg-electric-blue/10 px-2 py-0.5 rounded-full">
                                {t('wizard.multichain')}
                            </span>
                        }
                    >
                        <div className="flex items-center gap-3">
                            <span className="material-symbols-outlined text-txt-muted text-[24px]">account_balance_wallet</span>
                            <input
                                type="text"
                                value={formData.walletAddress}
                                onChange={(e) => setFormData(prev => ({ ...prev, walletAddress: e.target.value }))}
                                placeholder="0x..."
                                className="w-full bg-transparent border-none text-txt-heading font-mono text-xs placeholder:text-txt-muted focus:ring-0 focus:outline-none p-0"
                            />
                        </div>
                    </FormField>

                    {/* Chain/Token Toggles */}
                    {paymentSettings?.chains && (
                        <ChainTokenList
                            chains={paymentSettings.chains}
                            selectedChains={formData.selectedChains}
                            selectedTokens={formData.selectedTokens}
                            onToggleChain={handleToggleChain}
                            onToggleToken={handleToggleToken}
                        />
                    )}
                </div>

                {/* Actions (Sticky glassmorphic bar) */}
                <div className="sticky bottom-0 pt-4 pb-6 px-6 flex gap-3 w-full backdrop-blur-xl" style={{ background: 'var(--sticky-bg)' }}>
                    <button
                        onClick={onCancel}
                        className="flex-1 font-bold py-4 rounded-xl transition-all active:scale-95 flex items-center justify-center text-txt-secondary"
                        style={{ background: 'var(--card-bg)' }}
                    >
                        {t('wizard.cancel')}
                    </button>
                    <button
                        onClick={onNext}
                        disabled={!formData.name}
                        className="flex-[2] bg-electric-blue disabled:bg-electric-blue/50 disabled:cursor-not-allowed hover:bg-electric-blue/90 text-white font-bold py-4 rounded-xl shadow-lg shadow-electric-blue/20 transition-all active:scale-95 flex items-center justify-center"
                    >
                        {t('wizard.continue')}
                    </button>
                </div>
            </div>
        </div>
    );
}
